import React, { useState, useEffect } from "react";
import axios from "axios";

import { API_BASE_URL } from '../../config';
import * as XLSX from "xlsx";
import { useAuth } from '../../components/shared/AuthContext';
import { PageLayout, Table, Button, DatePicker } from "../../components/ui";
import Loader from '../../components/ui/Loader';
import ReportHeader from "../../components/reports/ReportHeader";
import ReportFooter from "../../components/reports/ReportFooter";

const DailyCollectionReport = () => {
  const { roleName, data } = useAuth();
  const userBranchId = data?.branchId;

  // Get today's date in YYYY-MM-DD format for the date inputs
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // State
  const [fromDate, setFromDate] = useState(getTodayDate());
  const [toDate, setToDate] = useState(getTodayDate());
  const [transactions, setTransactions] = useState([]);
  const [unpaidCustomers, setUnpaidCustomers] = useState([]);
  const [totalDeposits, setTotalDeposits] = useState(0);
  const [viewMode, setViewMode] = useState("paid");
  const [fieldOfficers, setFieldOfficers] = useState([]);
  const [selectedFieldOfficer, setSelectedFieldOfficer] = useState("");
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedColumn, setSelectedColumn] = useState(null);

  // Server-side pagination for paid list
  const [paidPage,  setPaidPage]  = useState(1);
  const [paidLimit, setPaidLimit] = useState(50);
  const [paidTotal, setPaidTotal] = useState(0);
  const paidPageRef  = React.useRef(1);
  const paidLimitRef = React.useRef(50);
  React.useEffect(() => { paidPageRef.current  = paidPage;  }, [paidPage]);
  React.useEffect(() => { paidLimitRef.current = paidLimit; }, [paidLimit]);
  const paidTotalPages  = Math.ceil(paidTotal / paidLimit);
  const paidIndexFirst  = (paidPage - 1) * paidLimit;

  const isSuperAdmin = roleName === "superadmin";

  // Fetch branches for superadmin
  useEffect(() => {
    const fetchBranches = async () => {
      if (!isSuperAdmin) return;
      try {
        const response = await axios.get(`${API_BASE_URL}/api/getModelData/branch_details`);
        setBranches(response.data.data || []);
      } catch (error) {
        console.error("Error fetching branches:", error);
      }
    };
    fetchBranches();
  }, [isSuperAdmin]);

  // Fetch field officers based on selected branch (or user's branch)
  useEffect(() => {
    const fetchFieldOfficers = async () => {
      const branchIdToUse = isSuperAdmin ? selectedBranch : userBranchId;
      if (!branchIdToUse) {
        setFieldOfficers([]);
        return;
      }

      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/getModelData/staff_details?filter={"branchId":${branchIdToUse}}`
        );

        const allStaff = Array.isArray(response.data.data) ? response.data.data : [];

        // Include Admin(2), Field Officer(3), Data Entry(4), Manager(5)
        const relevantRoles = [2, 3, 4, 5];
        const filteredFieldOfficers = allStaff.filter(
          (staff) => relevantRoles.includes(Number(staff.roleId))
        );

        setFieldOfficers(filteredFieldOfficers);
      } catch (error) {
        console.error("Error fetching field officers:", error);
      }
    };
    fetchFieldOfficers();
  }, [userBranchId, selectedBranch, isSuperAdmin]);

  const handleBranchChange = (e) => {
    setSelectedBranch(e.target.value);
    setSelectedFieldOfficer(""); // Reset field officer when branch changes
  };

  const handleFieldOfficerChange = (e) => {
    setSelectedFieldOfficer(e.target.value);
  };

  // ── Core fetch (called by useEffect and pagination buttons) ──────────────
  const doFetch = React.useCallback(async (page, limit) => {
    const branchIdToUse = isSuperAdmin ? selectedBranch : userBranchId;
    if (!fromDate || !toDate || !branchIdToUse) return;

    setLoading(true);
    try {
      const params = { fromDate, toDate, branchId: branchIdToUse, page, limit };

      if (selectedFieldOfficer) {
        const selectedStaff = fieldOfficers.find(s => String(s.id) === String(selectedFieldOfficer));
        const roleId = selectedStaff ? Number(selectedStaff.roleId) : null;
        if (roleId === 3) {
          params.fieldOfficerId = selectedFieldOfficer;
          params.managerId      = selectedFieldOfficer;
        } else {
          params.managerId = selectedFieldOfficer;
        }
      } else {
        params.managerRoleId = 2;
      }

      const response = await axios.get(
        `${API_BASE_URL}/api/getDailyCollectionReportOptimized`,
        { params }
      );

      if (response.data.status === 'success') {
        const { paid, paidTotal, unpaid } = response.data.data;
        setTransactions(paid || []);
        setPaidTotal(paidTotal || 0);
        setUnpaidCustomers(unpaid || []);
        setTotalDeposits((paid || []).reduce((s, tx) => s + (Number(tx.amount) || 0), 0));
      }
    } catch (error) {
      console.error('Error fetching report data:', error);
      setTransactions([]);
      setPaidTotal(0);
      setUnpaidCustomers([]);
      setTotalDeposits(0);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, userBranchId, selectedBranch, isSuperAdmin, selectedFieldOfficer, fieldOfficers]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPaidPage(1);
    doFetch(1, paidLimitRef.current);
  }, [fromDate, toDate, userBranchId, selectedBranch, isSuperAdmin, selectedFieldOfficer, fieldOfficers]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const exportToExcel = () => {
    const dataToExport = viewMode === "paid" ?
      transactions.map((tx, index) => ({
        "S.No": index + 1,
        Date: formatDate(tx.date),
        "Customer ID": tx.customerUniqueId,
        "Customer Name": tx.customerName,
        "Transaction ID": tx.transactionId,
        "Cash Type": tx.cash_type,
        Amount: tx.amount,
        Narration: tx.narration,
        "Closing Balance": tx.closingBalance,
        "Staff Name": tx.staffName || 'N/A',
      })) :
      unpaidCustomers.map((customer, index) => ({
        "S.No": index + 1,
        "Customer ID": customer.customerUniqueId,
        "Customer Name": customer.name,
        "Mobile Number": customer.mobileNumber,
        "Status": "Not Paid"
      }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(wb, `${viewMode === "paid" ? "Paid" : "pending"}_Customers_${formatDate(new Date())}.xlsx`);
  };

  return (
    <PageLayout className="h-auto min-h-[calc(100vh-120px)] overflow-y-auto pb-4">
      {/* Reusable Header: Removes title & subtitle, aligns filters & actions on a single row */}
      <ReportHeader
        filters={
          <>
            {/* From Date */}
            <div className="w-[145px] mt-1">
              <DatePicker
                label="From Date"
                selected={fromDate}
                onChange={(date) => setFromDate(date ? date.toISOString().split('T')[0] : '')}
                placeholder="DD-MM-YYYY"
              />
            </div>

            {/* To Date */}
            <div className="w-[145px] mt-1">
              <DatePicker
                label="To Date"
                selected={toDate}
                onChange={(date) => setToDate(date ? date.toISOString().split('T')[0] : '')}
                placeholder="DD-MM-YYYY"
              />
            </div>

            {/* Branch Selection for Superadmin */}
            {isSuperAdmin && (
              <div className="relative min-w-[170px] mt-1">
                <label className="absolute -top-2 left-3 bg-white px-1.5 text-xs font-bold tracking-wide text-ink-400 select-none z-10 pointer-events-none">
                  Branch
                </label>
                <div className="relative">
                  <select
                    value={selectedBranch}
                    onChange={handleBranchChange}
                    className="form-input bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary focus:ring-1 focus:ring-primary/20 text-ink-700 h-[38px] text-xs pl-3.5 pr-8 w-full appearance-none cursor-pointer focus:outline-none transition-all font-medium"
                  >
                    <option value="">Select Branch</option>
                    {branches.map((branch) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.branchName}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-ink-400">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>
            )}

            {/* Field Officer Selection */}
            <div className="relative min-w-[170px] mt-1">
              <label className="absolute -top-2 left-3 bg-white px-1.5 text-xs font-bold tracking-wide text-ink-400 select-none z-10 pointer-events-none">
                Staff Name
              </label>
              <div className="relative">
                <select
                  value={selectedFieldOfficer}
                  onChange={handleFieldOfficerChange}
                  className="form-input bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary focus:ring-1 focus:ring-primary/20 text-ink-700 h-[38px] text-xs pl-3.5 pr-8 w-full appearance-none cursor-pointer focus:outline-none transition-all font-medium"
                >
                  <option value="">Select Staff</option>
                  {fieldOfficers.length === 0 && (
                    <option disabled>No officers found</option>
                  )}
                  {fieldOfficers.map((officer) => (
                    <option key={officer.id} value={officer.id}>
                      {officer.name ? officer.name : officer.username || "Unnamed Officer"}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-ink-400">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center h-[38px] bg-[#F1F5F9] p-1 rounded-lg mt-1">
              <button
                type="button"
                className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${
                  viewMode === "paid"
                    ? "bg-white text-primary shadow-sm"
                    : "text-ink-500 hover:text-ink-900"
                }`}
                onClick={() => setViewMode("paid")}
              >
                Paid
              </button>
              <button
                type="button"
                className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${
                  viewMode === "unpaid"
                    ? "bg-white text-primary shadow-sm"
                    : "text-ink-500 hover:text-ink-900"
                }`}
                onClick={() => setViewMode("unpaid")}
              >
                Unpaid
              </button>
            </div>
          </>
        }
        actions={
          <Button
            onClick={exportToExcel}
            variant="primary"
            size="sm"
            className="shadow-sm h-[38px] px-4 font-bold"
          >
            <i className="fa-solid fa-file-excel mr-1.5"></i>
            Export to Excel
          </Button>
        }
      />
      {loading && <Loader message="Loading report data..." />}

      {viewMode === "paid" ? (
        <div className="flex-grow flex flex-col min-h-0">
          <Table>
            <Table.Head>
              <tr>
                <Table.HeadCell
                  align="center"
                  sortable
                  sortDirection={selectedColumn === 'sno' ? 'desc' : undefined}
                  onSort={() => setSelectedColumn(selectedColumn === 'sno' ? null : 'sno')}
                >
                  S.No
                </Table.HeadCell>
                <Table.HeadCell
                  align="center"
                  sortable
                  sortDirection={selectedColumn === 'date' ? 'desc' : undefined}
                  onSort={() => setSelectedColumn(selectedColumn === 'date' ? null : 'date')}
                >
                  Date
                </Table.HeadCell>
                <Table.HeadCell
                  align="center"
                  sortable
                  sortDirection={selectedColumn === 'id' ? 'desc' : undefined}
                  onSort={() => setSelectedColumn(selectedColumn === 'id' ? null : 'id')}
                >
                  Customer ID
                </Table.HeadCell>
                <Table.HeadCell
                  align="left"
                  sortable
                  sortDirection={selectedColumn === 'name' ? 'desc' : undefined}
                  onSort={() => setSelectedColumn(selectedColumn === 'name' ? null : 'name')}
                >
                  Customer Name
                </Table.HeadCell>
                <Table.HeadCell
                  align="center"
                  sortable
                  sortDirection={selectedColumn === 'cashtype' ? 'desc' : undefined}
                  onSort={() => setSelectedColumn(selectedColumn === 'cashtype' ? null : 'cashtype')}
                >
                  Cash Type
                </Table.HeadCell>
                <Table.HeadCell
                  align="center"
                  sortable
                  sortDirection={selectedColumn === 'txid' ? 'desc' : undefined}
                  onSort={() => setSelectedColumn(selectedColumn === 'txid' ? null : 'txid')}
                >
                  Transaction Id
                </Table.HeadCell>
                <Table.HeadCell
                  align="right"
                  sortable
                  sortDirection={selectedColumn === 'amount' ? 'desc' : undefined}
                  onSort={() => setSelectedColumn(selectedColumn === 'amount' ? null : 'amount')}
                >
                  Amount
                </Table.HeadCell>
                <Table.HeadCell
                  align="center"
                  sortable
                  sortDirection={selectedColumn === 'narration' ? 'desc' : undefined}
                  onSort={() => setSelectedColumn(selectedColumn === 'narration' ? null : 'narration')}
                >
                  Narration
                </Table.HeadCell>
                <Table.HeadCell
                  align="center"
                  sortable
                  sortDirection={selectedColumn === 'time' ? 'desc' : undefined}
                  onSort={() => setSelectedColumn(selectedColumn === 'time' ? null : 'time')}
                >
                  Time
                </Table.HeadCell>
                <Table.HeadCell
                  align="right"
                  sortable
                  sortDirection={selectedColumn === 'closing' ? 'desc' : undefined}
                  onSort={() => setSelectedColumn(selectedColumn === 'closing' ? null : 'closing')}
                >
                  Closing Balance
                </Table.HeadCell>
                <Table.HeadCell
                  align="left"
                  sortable
                  sortDirection={selectedColumn === 'staff' ? 'desc' : undefined}
                  onSort={() => setSelectedColumn(selectedColumn === 'staff' ? null : 'staff')}
                >
                  Staff Name
                </Table.HeadCell>
              </tr>
            </Table.Head>
            <Table.Body>
              {transactions.length > 0 ? (
                transactions.map((tx, index) => (
                  <Table.Row key={tx.id}>
                    <Table.Cell align="center">{paidIndexFirst + index + 1}</Table.Cell>
                    <Table.Cell align="center">{formatDate(tx.date)}</Table.Cell>
                    <Table.Cell align="center" className="font-medium">{tx.customerUniqueId}</Table.Cell>
                    <Table.Cell align="left">{tx.customerName}</Table.Cell>
                    <Table.Cell align="center">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${
                        tx.cash_type === 'cash'
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-indigo-100 text-indigo-800'
                      }`}>
                        {tx.cash_type}
                      </span>
                    </Table.Cell>
                    <Table.Cell align="center">{tx.transactionId}</Table.Cell>
                    <Table.AmountCell align="right">
                      {parseFloat(tx.amount || 0)}
                    </Table.AmountCell>
                    <Table.Cell align="center">{tx.narration}</Table.Cell>
                    <Table.Cell align="center" className="text-xs text-ink-500">
                      {tx.createdAt ? new Date(tx.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true }) : 'N/A'}
                    </Table.Cell>
                    <Table.AmountCell align="right" className="text-[#0369a1]">
                      {parseFloat(tx.closingBalance || 0)}
                    </Table.AmountCell>
                    <Table.Cell align="left">
                      <span className="bg-[#e8eaf6] text-[#283593] rounded px-2 py-0.5 text-xs font-semibold">
                        {tx.staffName || 'N/A'}
                      </span>
                    </Table.Cell>
                  </Table.Row>
                ))
              ) : (
                <Table.EmptyRow colSpan={11} message="No transactions found" />
              )}
            </Table.Body>
          </Table>

          {/* Combined Footer: Standalone Pagination on left, Summary Card on right */}
          <ReportFooter
            currentPage={paidPage}
            totalPages={paidTotalPages}
            totalCount={paidTotal}
            rowsPerPage={paidLimit}
            onPageChange={(p) => {
              setPaidPage(p);
              doFetch(p, paidLimitRef.current);
            }}
            onRowsPerPageChange={(newLimit) => {
              setPaidLimit(newLimit);
              setPaidPage(1);
              paidLimitRef.current = newLimit;
              doFetch(1, newLimit);
            }}
            pageSizeOptions={[25, 50, 100, 200]}
            totals={[
              {
                label: "TOTAL COLLECTIONS",
                value: totalDeposits,
                variant: "emerald",
              },
            ]}
          />
        </div>
      ) : (
        <div className="flex-grow flex flex-col min-h-0">
          <Table>
            <Table.Head>
              <tr>
                <Table.HeadCell
                  align="center"
                  sortable
                  sortDirection={selectedColumn === 'sno_unpaid' ? 'desc' : undefined}
                  onSort={() => setSelectedColumn(selectedColumn === 'sno_unpaid' ? null : 'sno_unpaid')}
                >
                  S.No
                </Table.HeadCell>
                <Table.HeadCell
                  align="center"
                  sortable
                  sortDirection={selectedColumn === 'id_unpaid' ? 'desc' : undefined}
                  onSort={() => setSelectedColumn(selectedColumn === 'id_unpaid' ? null : 'id_unpaid')}
                >
                  Customer ID
                </Table.HeadCell>
                <Table.HeadCell
                  align="left"
                  sortable
                  sortDirection={selectedColumn === 'name_unpaid' ? 'desc' : undefined}
                  onSort={() => setSelectedColumn(selectedColumn === 'name_unpaid' ? null : 'name_unpaid')}
                >
                  Customer Name
                </Table.HeadCell>
                <Table.HeadCell
                  align="center"
                  sortable
                  sortDirection={selectedColumn === 'phone_unpaid' ? 'desc' : undefined}
                  onSort={() => setSelectedColumn(selectedColumn === 'phone_unpaid' ? null : 'phone_unpaid')}
                >
                  Mobile Number
                </Table.HeadCell>
                <Table.HeadCell
                  align="center"
                  sortable
                  sortDirection={selectedColumn === 'status_unpaid' ? 'desc' : undefined}
                  onSort={() => setSelectedColumn(selectedColumn === 'status_unpaid' ? null : 'status_unpaid')}
                >
                  Status
                </Table.HeadCell>
              </tr>
            </Table.Head>
            <Table.Body>
              {unpaidCustomers.length > 0 ? (
                unpaidCustomers.map((customer, index) => (
                  <Table.Row key={customer.customerUniqueId}>
                    <Table.Cell align="center">{index + 1}</Table.Cell>
                    <Table.Cell align="center" className="font-medium">{customer.customerUniqueId}</Table.Cell>
                    <Table.Cell align="left">{customer.name}</Table.Cell>
                    <Table.Cell align="center">{customer.mobileNumber}</Table.Cell>
                    <Table.Cell align="center">
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-red-100 text-red-800">
                        Not Paid
                      </span>
                    </Table.Cell>
                  </Table.Row>
                ))
              ) : (
                <Table.EmptyRow colSpan={5} message="No unpaid customers found" />
              )}
            </Table.Body>
          </Table>

          {/* Summary Card for Unpaid Customers */}
          <ReportFooter
            totals={[
              {
                label: "UNPAID CUSTOMERS",
                value: unpaidCustomers.length,
                variant: "red",
                formatCurrency: false,
              },
            ]}
          />
        </div>
      )}
    </PageLayout>
  );
};

export default DailyCollectionReport;